<template>
  <div class="w-full space-y-6 [&_.text-error]:text-[#7f1d1d] dark:[&_.text-error]:text-[#fecaca]">
    <div class="space-y-1">
      <h2 class="text-xl font-medium tracking-tight text-zinc-900 dark:text-zinc-50">
        {{ isSignUp ? 'Crear cuenta' : 'Iniciar sesión' }}
      </h2>
      <p class="text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
        {{ isSignUp ? 'Registra un acceso para el equipo de planta.' : 'Usa tu correo de planta para continuar.' }}
      </p>
    </div>

    <UForm
      :key="isSignUp ? 'signup' : 'login'"
      :schema="schema"
      :state="state"
      method="post"
      class="space-y-5"
      @submit="handleSubmit"
    >
      <Transition name="login-field">
        <UFormField v-if="isSignUp" label="Nombre" name="name">
          <UInput
            v-model="state.name"
            size="lg"
            class="w-full"
            placeholder="Ingresa tu nombre"
            autocomplete="name"
          />
        </UFormField>
      </Transition>

      <UFormField label="Email" name="email">
        <UInput
          v-model="state.email"
          type="email"
          size="lg"
          class="w-full"
          placeholder="Ingresa tu email"
          autocomplete="email"
        />
      </UFormField>

      <UFormField label="Contraseña" name="password">
        <UInput
          v-model="state.password"
          :type="showPassword ? 'text' : 'password'"
          size="lg"
          class="w-full"
          placeholder="Ingresa tu contraseña"
          :autocomplete="isSignUp ? 'new-password' : 'current-password'"
        >
          <template #trailing>
            <UButton
              type="button"
              color="neutral"
              variant="link"
              size="sm"
              :icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
              :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
              :aria-pressed="showPassword"
              @click="showPassword = !showPassword"
            />
          </template>
        </UInput>
      </UFormField>

      <UCheckbox
        v-if="!isSignUp"
        v-model="rememberMe"
        label="Recordarme"
      />

      <UButton
        type="submit"
        block
        size="md"
        color="primary"
        class="transition-transform active:scale-[0.98]"
        :loading="loading"
        :disabled="loading"
      >
        {{ isSignUp ? 'Registrarse' : 'Continuar' }}
      </UButton>

      <UAlert
        v-if="error"
        icon="i-lucide-circle-alert"
        color="error"
        variant="soft"
        :title="error"
      />
    </UForm>

    <p class="text-sm text-zinc-500 dark:text-zinc-400">
      {{ isSignUp ? '¿Ya tienes cuenta?' : '¿No tienes cuenta?' }}
      <UButton
        type="button"
        variant="link"
        class="h-auto p-0 text-sm font-medium"
        @click="toggleMode"
      >
        {{ isSignUp ? 'Inicia sesión' : 'Regístrate' }}
      </UButton>
    </p>
  </div>
</template>

<script setup lang="ts">
import { loginSchema, registerSchema } from '~/utils/auth.schema'
import { safeInternalPath } from '~/utils/safe-redirect'

const { signIn, signUp } = useAuth()

const isSignUp = ref(false)
const loading = ref(false)
const error = ref<string | null>(null)
const rememberMe = ref(false)
const showPassword = ref(false)

const state = reactive({
  name: '',
  email: '',
  password: ''
})

const schema = computed(() => isSignUp.value ? registerSchema : loginSchema)

function toggleMode() {
  isSignUp.value = !isSignUp.value
  error.value = null
  rememberMe.value = false
  showPassword.value = false
  state.name = ''
  state.email = ''
  state.password = ''
}

async function handleSubmit() {
  loading.value = true
  error.value = null

  try {
    const result = isSignUp.value
      ? await signUp(state.email, state.password, state.name)
      : await signIn(state.email, state.password, rememberMe.value)

    if (result.success) {
      const route = useRoute()
      await navigateTo(safeInternalPath(route.query.redirect))
    } else {
      error.value = result.error || 'Error al procesar la solicitud'
    }
  } catch (err: unknown) {
    error.value = err instanceof Error ? err.message : 'Error inesperado'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-field-enter-active,
.login-field-leave-active {
  transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.login-field-enter-from,
.login-field-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (prefers-reduced-motion: reduce) {
  .login-field-enter-active,
  .login-field-leave-active {
    transition: none;
  }
}
</style>
